import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { formatCount } from '../format';
import type { PublicProfile } from '../types';
import { Icon } from './Icon';

/** Thumbs up · Let's Jam · Message — the action row at the top of a public profile. */
export function ProfileActions({ u, onChange }: { u: PublicProfile; onChange: (patch: Partial<PublicProfile>) => void }) {
  const [busy, setBusy] = useState<'like' | 'jam' | 'msg' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const nav = useNavigate();

  const run = async (kind: 'like' | 'jam' | 'msg', fn: () => Promise<void>) => {
    setBusy(kind);
    setError(null);
    try { await fn(); } catch (e: any) { setError(e.message); } finally { setBusy(null); }
  };

  const toggleLike = () => run('like', async () => {
    const r = u.likedByMe ? await api.unlike(u.id) : await api.like(u.id);
    onChange({ likes: r.likes, likedByMe: r.likedByMe });
  });
  // One per person, enforced server-side (409 on a repeat). No navigation: the note says it went out.
  const jam = () => run('jam', async () => {
    await api.interest(u.id);
    onChange({ interestSent: true });
    setNote(`Sent! ${u.displayName} will see it in their messages.`);
    window.setTimeout(() => setNote(null), 4000);
  });
  const message = () => run('msg', async () => {
    const { roomId } = await api.openDm(u.id);
    nav(`/messages/${roomId}`);
  });

  return (
    <div className="stack">
      <div className="actions">
        <button className={`btn secondary ${u.likedByMe ? 'liked' : ''}`} disabled={busy !== null} onClick={toggleLike} aria-pressed={u.likedByMe}>
          <Icon name="thumbs" size={20} /> {formatCount(u.likes)}
        </button>
        <button className="btn secondary" disabled={busy !== null || u.interestSent} onClick={jam}>
          <Icon name="jam" size={20} /> {u.interestSent ? 'Jam sent' : "Let's Jam"}
        </button>
        <button className="btn secondary" disabled={busy !== null} onClick={message}><Icon name="messages" size={20} /> Message</button>
      </div>
      {error && <div className="error">{error}</div>}
      {note && <div className="ok">{note}</div>}
    </div>
  );
}
